// HubTopBar — the ClientHub top bar (Epic 3 · S3.2).
//
// `Insider Perks  [ClientHub ▾]  search · inbox · timer · user`
//
// The [ClientHub ▾] is the workspace switcher from Epic 2. The scope bar reads
// `scope_type` from the workspace provider. Search/inbox/timer are placeholders
// here — the actual surfaces are Epic 4 (Shell-level surfaces). The top bar is
// the same on every hub page.

import React, { useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useWorkspace } from "../../../contexts/WorkspaceContext";
import { useShellSurface } from "../../../contexts/ShellSurfaceContext";
import { useActiveRole } from "../../../hooks/useActiveRole";
import WorkspaceSwitcher from "../WorkspaceSwitcher";
import MentionsBadge from "./MentionsBadge";

// What the switch back to the analytics dashboard is called here. Product
// (2026-10-12) wants it to read "Dashboard" — the symmetric label to the
// dashboard side's "Client Hub", so the two surfaces are one segmented control:
// from the hub you go to "Dashboard", from the dashboard you go to "Client Hub".
const DASHBOARD_TOGGLE_LABEL = "Dashboard";

// Scope type → human-readable label for the scope bar.
const SCOPE_LABELS = {
  fixed: "All clients",
  property_picker: "One property",
  market: "Market view",
};

export default function HubTopBar({ className = "" }) {
  const navigate = useNavigate();
  const { scopeType } = useWorkspace();
  const { openInbox, openSearch, openTimer } = useShellSurface();
  // 2026-09-14 — Analytics ↔ Client Hub toggle (hub side). Admins / owners
  // (isAdmin) who are NOT Client-Hub-only staff get an "Analytics" switch back to
  // the analytics dashboard. Agency-internal staff (isStaff) never see this —
  // they're Client-Hub-only and have no dashboard option.
  // 2026-09-30 — staff must have NO way back to the dashboard from the Client
  // Hub, so the additive canOpenClientDashboard exception was removed: the gate
  // became just (isAdmin && !isStaff). The six full-access staff (isStaff=true)
  // no longer got the toggle; org_owner/org_admin and platform admin still did.
  // 2026-10-12 — staff_owner (promoted to a toggle role 2026-10-06) and
  // staff_manager (promoted 2026-10-07) land on /dashboard on login and can
  // switch into the Client Hub, so they MUST have a way back. They are NOT
  // admins (numeric 2 → isAdmin=false) and NOT hub-only staff
  // (isStaffRoleName=false → isStaff=false), so the (isAdmin && !isStaff) gate
  // hid the toggle from them — they could enter the hub from the dashboard but
  // could not return. Restore the additive canOpenClientDashboard exception so
  // the gate mirrors AdminHeader's canToggleToHub = (isAdmin && !isStaff) ||
  // canOpenClientDashboard and the two surfaces stay one persistent segmented
  // control (the menu persists in both directions). Hub-only staff
  // (staff_member / staff_limited) keep isStaff=true with
  // canOpenClientDashboard=false, so they still see no toggle.
  const { isAdmin, isStaff, canOpenClientDashboard } = useActiveRole();
  const canToggleToDashboard = (!!isAdmin && !isStaff) || canOpenClientDashboard;

  // Profile avatar — mirrors AdminHeader's avatar exactly so the hub and the
  // dashboard behave identically: a real <Link to="/profile"> (not a button +
  // navigate) that shows the user's avatar image when present (localStorage
  // 'avatar') or their real initials (from localStorage 'username'). Same
  // source of truth, same label, same active-page ring as the dashboard.
  const location = useLocation();
  const username =
    localStorage.getItem("username") &&
    localStorage.getItem("username") !== "undefined"
      ? localStorage.getItem("username")
      : "username";
  const avatar = localStorage.getItem("avatar") || null;
  const initials = useMemo(
    () =>
      username
        ? username
            .split(" ")
            .map((n) => n[0]?.toUpperCase() || "")
            .join("")
            .slice(0, 2)
        : "",
    [username],
  );
  const isProfileActive = location.pathname === "/hub/profile";

  // Only explicitly-mapped scope types render a human label. An unmapped
  // scope_type (e.g. "client_filter") renders nothing — never the raw internal
  // type string, which would leak database naming into the UI.
  const scopeLabel = SCOPE_LABELS[scopeType] || "";

  return (
    <header
      className={`flex h-14 items-center gap-4 border-b border-gray-200 bg-white px-4 dark:border-gray-800 dark:bg-gray-900 ${className}`}
      data-testid="hub-topbar"
    >
      {/* Workspace switcher. The "Insider Perks" wordmark shows ONLY on mobile
          (<768px): there the sidebar — which carries the brand on desktop — is a
          hidden drawer, so the top bar is the only brand on screen. On desktop the
          sidebar already shows it, so repeating it here reads as duplicate branding. */}
      <div className="flex items-center gap-3">
        <span className="md:hidden text-sm font-bold text-gray-900 dark:text-gray-100">
          Insider Perks
        </span>
        <WorkspaceSwitcher />
      </div>

      {/* Analytics ↔ Client Hub toggle — the "back to the analytics dashboard" side.
          Mirrors the "Client Hub" link in AdminHeader so the two surfaces read as one
          segmented control across the app. It carries a border so it reads as a
          control next to the workspace switcher, never as the page's title. */}
      {canToggleToDashboard && (
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          title={`Switch to ${DASHBOARD_TOGGLE_LABEL}`}
          data-testid="hub-dashboard-toggle"
        >
          {DASHBOARD_TOGGLE_LABEL}
        </button>
      )}

      {/* Scope bar — reads scope_type from the workspace provider */}
      {scopeLabel && (
        <span className="hidden items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400 sm:inline-flex">
          {scopeLabel}
        </span>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Shell surfaces — Epic 4. Search binds `/` globally; the others open here. */}
      <div className="flex items-center gap-3">
        {/* Search — Epic 4 · S4.4 (also opened with `/` from anywhere) */}
        <button
          type="button"
          onClick={openSearch}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Search"
          title="Search (press /)"
          data-testid="hub-search-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>

        {/* Mentions — Epic 4 · S4.3 (badge + popover; opens inbox at the thread) */}
        <MentionsBadge />

        {/* Inbox — Epic 4 · S4.1 */}
        <button
          type="button"
          onClick={() => openInbox()}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Inbox"
          title="Inbox"
          data-testid="hub-inbox-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
            <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
          </svg>
        </button>

        {/* Timer — Epic 4 · S4.2 */}
        <button
          type="button"
          onClick={openTimer}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Timer"
          title="Timer"
          data-testid="hub-timer-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="13" r="8" />
            <path d="M12 9v4l2 2" />
            <path d="M5 3L2 6" />
            <path d="M22 6l-3-3" />
          </svg>
        </button>

        {/* Profile — a real <Link to="/profile">, the same as the AdminHeader
            avatar on the dashboard: shows the user's avatar image when present,
            otherwise their initials. A true anchor so middle-click / "open in
            new tab" / hover-URL all work, matching the dashboard behavior. */}
        <Link
          to="/hub/profile"
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white overflow-hidden shrink-0 transition-opacity hover:opacity-90 ${
            isProfileActive
              ? "ring-2 ring-[#32B1FF] ring-offset-2 dark:ring-offset-gray-900"
              : ""
          } ${avatar ? "" : "bg-[#32B1FF]"}`}
          title="View Profile"
          aria-label="View Profile"
          data-testid="hub-profile-link"
        >
          {avatar ? (
            <img src={avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </Link>
      </div>
    </header>
  );
}
